import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchEmpresas, setEmpresaActiva } from "@/api/facturas";
import { useAuth } from "@/contexts/AuthContext";
import type { Empresa } from "@/types/api";

interface EmpresaContextValue {
  empresas: Empresa[];
  empresaActiva: number | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  select: (empresaId: number) => Promise<void>;
}

const EmpresaContext = createContext<EmpresaContextValue | undefined>(undefined);

export const EmpresaProvider = ({ children }: { children: ReactNode }) => {
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresaActivaLocal, setEmpresaActivaLocal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user, refresh: refreshUser } = useAuth();

  const empresaActiva = empresaActivaLocal ?? user?.empresa_id ?? null;

  const refresh = useCallback(async () => {
    if (!user) {
      setEmpresas([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEmpresas();
      setEmpresas(res.data ?? []);
      setEmpresaActivaLocal(res.empresa_activa ?? user?.empresa_id ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar empresas");
      setEmpresas([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const select = useCallback(
    async (empresaId: number) => {
      await setEmpresaActiva(empresaId);
      await refreshUser();
      await refresh();
    },
    [refreshUser, refresh],
  );

  useEffect(() => {
    if (user) {
      setEmpresaActivaLocal(user.empresa_id ?? null);
      void refresh();
    } else {
      setEmpresas([]);
      setEmpresaActivaLocal(null);
      setError(null);
    }
  }, [user, refresh]);

  const value = useMemo<EmpresaContextValue>(
    () => ({ empresas, empresaActiva, loading, error, refresh, select }),
    [empresas, empresaActiva, loading, error, refresh, select],
  );

  return (
    <EmpresaContext.Provider value={value}>{children}</EmpresaContext.Provider>
  );
};

export const useEmpresa = (): EmpresaContextValue => {
  const ctx = useContext(EmpresaContext);
  if (!ctx) {
    throw new Error("useEmpresa debe usarse dentro de EmpresaProvider");
  }
  return ctx;
};
